class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const sectionsToRender = this.getSectionsToRender();
    const fallbackSectionsUrl = new URL(window.location.href);
    const requestedSections = sectionsToRender.map((section) => section.section || section.id);
    const parsedSections = parsedState.sections || {};
    const needsFallbackSections = sectionsToRender.some((section) => !parsedSections[section.section || section.id]);

    fallbackSectionsUrl.searchParams.set('sections', requestedSections.join(','));

    const renderSections = (sections) => {
      sectionsToRender.forEach((section) => {
        const sectionTarget = document.getElementById(section.id);
        const sectionHtml = sections[section.section || section.id];

        if (!sectionTarget || !sectionHtml) return;

        sectionTarget.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      });

      this.updateUpsell(parsedState);

      if (this.header) this.header.reveal();
      this.open();
    };

    if (!needsFallbackSections) {
      renderSections(parsedSections);
      return;
    }

    fetch(fallbackSectionsUrl.toString())
      .then((response) => response.json())
      .then((fallbackSections) => {
        renderSections({ ...parsedSections, ...fallbackSections });
      })
      .catch((error) => {
        console.error(error);
        renderSections(parsedSections);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        section: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
        section: 'cart-notification-button',
        selector: '#shopify-section-cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const selectedElement = parsedHtml.querySelector(selector);

    return selectedElement ? selectedElement.innerHTML : html;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateUpsell(parsedState) {
    const upsellContainer = document.getElementById('cart-notification-upsell');
    if (!upsellContainer) return;

    upsellContainer.hidden = true;
    upsellContainer.innerHTML = '';

    const boxVariantId = Number(this.notification.dataset.two8BoxVariantId || 0);
    const movementVariantId = Number(this.notification.dataset.two8MovementVariantId || 0);
    const superVariantId = Number(this.notification.dataset.two8SuperVariantId || 0);
    const movementAddOnVariantId = Number(this.notification.dataset.two8UpsellMovementVariantId || 0);
    const superAddOnVariantId = Number(this.notification.dataset.two8UpsellSuperVariantId || 0);
    const coachUrl = this.notification.dataset.two8CoachUrl || '/pages/game-changers';
    const addedVariantId = Number(parsedState.id || parsedState.variant_id || 0);

    if (!boxVariantId || addedVariantId !== boxVariantId) return;
    if (!movementAddOnVariantId || !superAddOnVariantId) return;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const extraBandVariantIds = [
          movementVariantId,
          superVariantId,
          movementAddOnVariantId,
          superAddOnVariantId,
        ].filter(Boolean);

        const hasBox = cart.items.some((item) => Number(item.variant_id) === boxVariantId);
        const extraBandCount = cart.items.reduce((count, item) => {
          return extraBandVariantIds.includes(Number(item.variant_id)) ? count + item.quantity : count;
        }, 0);

        if (!hasBox || extraBandCount > 0) return;

        upsellContainer.innerHTML = `
          <p class="cart-notification-upsell__eyebrow">Box-Owner Add-On</p>
          <h3 class="cart-notification-upsell__title h4">Most box buyers add one more band.</h3>
          <p class="cart-notification-upsell__copy">Choose one exclusive add-on price now. An extra band gives you more progression, more pairing options, and more ways to use the system without jumping into a bulk order.</p>
          <div class="cart-notification-upsell__options">
            <button type="button" class="button button--primary cart-notification-upsell__button" data-two8-upsell-variant="${movementAddOnVariantId}">
              Add Movement Band for $14
            </button>
            <button type="button" class="button button--secondary cart-notification-upsell__button" data-two8-upsell-variant="${superAddOnVariantId}">
              Add Super Band for $16
            </button>
          </div>
          <p class="cart-notification-upsell__meta">Exclusive box-owner add-on price. One extra band per order. Need more for athletes or clients? <a href="${coachUrl}">See coach and team options</a>.</p>
        `;

        this.bindUpsellButtons(upsellContainer);
        upsellContainer.hidden = false;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  bindUpsellButtons(container) {
    container.querySelectorAll('[data-two8-upsell-variant]').forEach((button) => {
      button.addEventListener('click', () => {
        const variantId = Number(button.dataset.two8UpsellVariant || 0);
        if (!variantId) return;

        container.querySelectorAll('[data-two8-upsell-variant]').forEach((actionButton) => {
          actionButton.setAttribute('disabled', 'disabled');
        });

        const formData = new FormData();
        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        formData.append('id', variantId);
        formData.append('quantity', 1);
        this.getSectionsToRender().forEach((section) => formData.append('sections', section.id));
        formData.append('sections_url', window.location.pathname);
        this.setActiveElement(button);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message || 'Unable to add upsell item');
            this.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            container.querySelectorAll('[data-two8-upsell-variant]').forEach((actionButton) => {
              actionButton.removeAttribute('disabled');
            });
          });
      });
    });
  }
}

customElements.define('cart-notification', CartNotification);
